import { useMutation, UseMutationResult } from "@tanstack/react-query";

import {
  API_AUTH_ENROLL_WEBAUTHN_FACTOR_URL,
  API_AUTH_ACTIVATE_WEBAUTHN_FACTOR_URL,
} from "constants/apiUrls";
import {
  WebAuthnEnrollmentOptions,
  UserCancelledError,
  base64urlToBuffer,
  bufferToBase64url,
} from "hooks/useEnrollWebAuthnFactor";
import { AuthTransaction } from "pages/register/types/authTransaction";

import useAuthTransactionApiClient from "./useAuthTransactionApiClient";

function useEnrollWebAuthnFactorViaTransaction(
  stateToken: string,
): UseMutationResult<AuthTransaction, Error, void> {
  const apiClient = useAuthTransactionApiClient();

  return useMutation({
    mutationFn: async (): Promise<AuthTransaction> => {
      // Step 1: Enroll via auth-transaction-authenticated endpoint.
      // The backend derives the user's Okta UID from transaction.user.id and
      // calls the Management API — no JWT required.
      const enrollResponse = await apiClient(
        API_AUTH_ENROLL_WEBAUTHN_FACTOR_URL,
        {
          method: "POST",
          body: JSON.stringify({ stateToken }),
        },
      );

      if (!enrollResponse.ok) {
        return Promise.reject(await enrollResponse.json());
      }

      const options: WebAuthnEnrollmentOptions = await enrollResponse.json();

      // Step 2: Trigger the native browser WebAuthn registration ceremony
      let rawCredential: Credential | null;
      try {
        rawCredential = await navigator.credentials.create({
          publicKey: {
            challenge: base64urlToBuffer(options.challenge),
            rp: options.rp,
            user: {
              id: base64urlToBuffer(options.user.id),
              name: options.user.name,
              displayName: options.user.displayName,
            },
            pubKeyCredParams:
              options.pubKeyCredParams as PublicKeyCredentialParameters[],
            authenticatorSelection: options.authenticatorSelection,
            excludeCredentials: options.excludeCredentials?.map((cred) => ({
              id: base64urlToBuffer(cred.id),
              type: cred.type as PublicKeyCredentialType,
            })),
          },
        });
      } catch (e) {
        if (e instanceof DOMException && e.name === "NotAllowedError") {
          throw new UserCancelledError();
        }
        throw e;
      }

      if (!rawCredential) {
        throw new UserCancelledError();
      }

      const credential = rawCredential as PublicKeyCredential;
      const attestationResponse =
        credential.response as AuthenticatorAttestationResponse;

      // Step 3: Activate via the same auth-transaction-authenticated endpoint
      const activateResponse = await apiClient(
        API_AUTH_ACTIVATE_WEBAUTHN_FACTOR_URL,
        {
          method: "POST",
          body: JSON.stringify({
            stateToken,
            factorId: options.factorId,
            attestation: bufferToBase64url(
              attestationResponse.attestationObject,
            ),
            clientData: bufferToBase64url(attestationResponse.clientDataJSON),
          }),
        },
      );

      if (!activateResponse.ok) {
        return Promise.reject(await activateResponse.json());
      }

      return activateResponse.json() as Promise<AuthTransaction>;
    },
  });
}

export default useEnrollWebAuthnFactorViaTransaction;
